// CS_50137_AUDIT_LOG_PAGE off only: used by the Audit log export dialog alone. Delete with the flag.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import Datetime from "react-datetime";
import ListDropdown from "../ListDropdown";
import PropTypes from "prop-types";
import utils from "utils";
import "./style.css";
import TimezoneBadge from "../TimezoneBadge/TimezoneBadge";

const RANGE_IDS = {
  TODAY: "today",
  LAST_SEVEN_DAYS: "lastSevenDays",
  LAST_MONTH: "lastMonth",
  LAST_THREE_MONTHS: "lastThreeMonths",
  CUSTOM: "custom",
};

const RANGE_ORDER = [
  RANGE_IDS.TODAY,
  RANGE_IDS.LAST_SEVEN_DAYS,
  RANGE_IDS.LAST_MONTH,
  RANGE_IDS.LAST_THREE_MONTHS,
  RANGE_IDS.CUSTOM,
];

const normalizeDate = (date) => {
  const d = new Date(date);
  d.setSeconds(0, 0);
  d.setMilliseconds(0);
  return d;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const createRangeFromDays = (days) => {
  const end = normalizeDate(new Date());
  const start = startOfDay(utils.setOffsetDate(end, 60 * 24 * days));
  end.setHours(23, 59, 59, 0);
  return { from: start, to: end };
};

const RANGE_CONFIG = {
  [RANGE_IDS.TODAY]: {
    labelKey: "auditLog.today",
    computeRange: () => {
      const to = normalizeDate(new Date());
      const from = startOfDay(to);
      to.setHours(23, 59, 59, 0);
      return { from, to };
    },
  },
  [RANGE_IDS.LAST_SEVEN_DAYS]: {
    labelKey: "auditLog.lastSevenDays",
    computeRange: () => createRangeFromDays(7),
  },
  [RANGE_IDS.LAST_MONTH]: {
    labelKey: "auditLog.lastMonth",
    computeRange: () => createRangeFromDays(30),
  },
  [RANGE_IDS.LAST_THREE_MONTHS]: {
    labelKey: "auditLog.lastThreeMonths",
    computeRange: () => createRangeFromDays(30 * 3),
  },
  [RANGE_IDS.CUSTOM]: {
    labelKey: "auditLog.custom",
  },
};

const getPresetRange = (rangeId) => {
  const cfg = RANGE_CONFIG[rangeId];
  return cfg?.computeRange ? cfg.computeRange() : undefined;
};

const formatHHmm = (d) => moment(d).format("HH:mm");

export default function TimeRangePicker({
  t,
  defaultRangeId = RANGE_IDS.LAST_THREE_MONTHS,
  defaultFrom = undefined,
  defaultTo = undefined,
  onChange = undefined,
  hideTime = false,
  hideTimezone = false,
}) {
  const resolvedTimezone = useMemo(
    () => utils.getIanaTimezone?.() || "UTC",
    []
  );

  const initialRange = useMemo(() => {
    const base = createRangeFromDays(90);
    if (defaultRangeId !== RANGE_IDS.CUSTOM) {
      return getPresetRange(defaultRangeId) || base;
    }
    return {
      from: defaultFrom ? normalizeDate(defaultFrom) : base.from,
      to: defaultTo ? normalizeDate(defaultTo) : base.to,
    };
  }, [defaultFrom, defaultTo, defaultRangeId]);

  const [rangeId, setRangeId] = useState(defaultRangeId);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [pendingFromTime, setPendingFromTime] = useState(
    formatHHmm(initialRange.from)
  );
  const [pendingToTime, setPendingToTime] = useState(
    formatHHmm(initialRange.to)
  );

  useEffect(() => {
    if (typeof onChange === "function") {
      onChange({ from, to, rangeId, timezone: resolvedTimezone });
    }
  }, [from, to, rangeId, resolvedTimezone, onChange]);

  const ensureCustom = useCallback(() => {
    if (rangeId !== RANGE_IDS.CUSTOM) setRangeId(RANGE_IDS.CUSTOM);
  }, [rangeId]);

  const updateDateWithTime = (baseDate, hours, minutes) => {
    const next = new Date(baseDate);
    next.setHours(hours, minutes, 0, 0);
    return normalizeDate(next);
  };

  const setBoth = (newFrom, newTo) => {
    setFrom(newFrom);
    setTo(newTo);
    setPendingFromTime(formatHHmm(newFrom));
    setPendingToTime(formatHHmm(newTo));
  };

  const handleRangeSelect = (id) => {
    setRangeId(id);
    if (id === RANGE_IDS.CUSTOM) return;
    const r = getPresetRange(id);
    if (r) setBoth(r.from, r.to);
  };

  const handleCustomFromChange = (value) => {
    if (!moment.isMoment(value) || !value.isValid()) return;
    ensureCustom();
    const datePart = normalizeDate(value.toDate());
    const tm = moment(pendingFromTime, "HH:mm");
    const updated = updateDateWithTime(datePart, tm.hour(), tm.minute());
    setFrom(updated);
    if (updated > to) {
      setTo(updated);
      setPendingToTime(formatHHmm(updated));
    }
    setPendingFromTime(formatHHmm(updated));
  };

  const handleCustomToChange = (value) => {
    if (!moment.isMoment(value) || !value.isValid()) return;
    ensureCustom();
    const datePart = normalizeDate(value.toDate());
    const tm = moment(pendingToTime, "HH:mm");
    const updated = updateDateWithTime(datePart, tm.hour(), tm.minute());
    if (updated < from) {
      setFrom(updated);
      setPendingFromTime(formatHHmm(updated));
    }
    setTo(updated);
    setPendingToTime(formatHHmm(updated));
  };

  const handleTimeFromChange = (value) => {
    if (!moment.isMoment(value) || !value.isValid()) return;
    ensureCustom();
    const fmt = value.format("HH:mm");
    setPendingFromTime(fmt);
    const updated = updateDateWithTime(from, value.hour(), value.minute());
    setFrom(updated);
    if (updated > to) {
      setTo(updated);
      setPendingToTime(formatHHmm(updated));
    }
  };

  const handleTimeToChange = (value) => {
    if (!moment.isMoment(value) || !value.isValid()) return;
    ensureCustom();
    const fmt = value.format("HH:mm");
    setPendingToTime(fmt);
    const updated = updateDateWithTime(to, value.hour(), value.minute());
    if (updated < from) {
      setFrom(updated);
      setPendingFromTime(formatHHmm(updated));
    }
    setTo(updated);
  };

  const isValidFromDate = (current) => current.isSameOrBefore(moment(to));
  const isValidToDate = (current) => current.isSameOrAfter(moment(from));

  const rangeItems = RANGE_ORDER.map((id) => ({
    id,
    text: t(RANGE_CONFIG[id].labelKey),
    command: () => handleRangeSelect(id),
    disabled: rangeId === id,
  }));

  const rangeDropdownTitle = t(
    RANGE_CONFIG[rangeId]?.labelKey ??
      RANGE_CONFIG[RANGE_IDS.LAST_THREE_MONTHS].labelKey
  );

  return (
    <div className="row align-items-start time-range-picker-wrapper">
      <div className="col-md-6 col-lg-4">
        <div className="form-group">
          <label>{t("auditLog.interval")}</label>
          <ListDropdown
            className="audit-log-dropdown"
            title={rangeDropdownTitle}
            listItems={rangeItems}
          />
        </div>
      </div>
      <div className="col-md-6 col-lg-8">
        <div className="row g-3">
          <div className="col-12 col-lg-4">
            <div className="time-range-col">
              <label className="w-100 text-center">{t("auditLog.from")}</label>
              <div className="audit-log-datetime-inputs">
                <Datetime
                  value={moment(from)}
                  onChange={handleCustomFromChange}
                  dateFormat="YYYY-MM-DD"
                  timeFormat={false}
                  inputProps={{
                    className: "input-date-log mr-2",
                    disabled: rangeId !== RANGE_IDS.CUSTOM,
                  }}
                  isValidDate={isValidFromDate}
                />
                {!hideTime && (
                  <Datetime
                    value={moment(pendingFromTime, "HH:mm")}
                    onChange={handleTimeFromChange}
                    dateFormat={false}
                    timeFormat="HH:mm"
                    inputProps={{
                      className: "input-time-log mr-0",
                      disabled: rangeId !== RANGE_IDS.CUSTOM,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className="time-range-col">
              <label className="w-100 text-center">{t("auditLog.to")}</label>
              <div className="audit-log-datetime-inputs">
                <Datetime
                  value={moment(to)}
                  onChange={handleCustomToChange}
                  dateFormat="YYYY-MM-DD"
                  timeFormat={false}
                  inputProps={{
                    className: "input-date-log mr-2",
                    disabled: rangeId !== RANGE_IDS.CUSTOM,
                  }}
                  isValidDate={isValidToDate}
                />
                {!hideTime && (
                  <Datetime
                    value={moment(pendingToTime, "HH:mm")}
                    onChange={handleTimeToChange}
                    dateFormat={false}
                    timeFormat="HH:mm"
                    inputProps={{
                      className: "input-time-log mr-0",
                      disabled: rangeId !== RANGE_IDS.CUSTOM,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          {!hideTimezone && (
            <div className="col-12 col-lg-4">
              <div className="timezone-col h-100 d-flex justify-content-center align-items-center">
                <TimezoneBadge localTime />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

TimeRangePicker.propTypes = {
  t: PropTypes.func.isRequired,
  defaultRangeId: PropTypes.oneOf(Object.values(RANGE_IDS)),
  defaultFrom: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string, // es. ISO date string
    PropTypes.number, // es. timestamp ms
  ]),
  defaultTo: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.number,
  ]),
  onChange: PropTypes.func,
  hideTime: PropTypes.bool,
  hideTimezone: PropTypes.bool,
};

